import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useSnapshot } from "valtio";
import {
  imageViewerStore,
  closeViewer,
  viewerNext,
  viewerPrev,
} from "../utils/imageViewer";

// Full-screen, in-app media overlay (WhatsApp-style). Renders the current item
// (image or video), supports prev/next via arrows, keyboard, and touch swipe.
const ImageViewer = () => {
  const { items, index } = useSnapshot(imageViewerStore);
  const open = items.length > 0;
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowRight") viewerNext();
      if (e.key === "ArrowLeft") viewerPrev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const current = items[index];
  const multiple = items.length > 1;

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? viewerNext : viewerPrev)();
    touchStartX.current = null;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[3000] bg-black/90 flex items-center justify-center select-none"
      onClick={closeViewer}
      onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
      onTouchEnd={onTouchEnd}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          closeViewer();
        }}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {multiple && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {index + 1} / {items.length}
        </div>
      )}

      {multiple && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            viewerPrev();
          }}
          className="absolute left-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Previous"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {current?.type === "video" ? (
        <video
          src={current.url}
          controls
          autoPlay
          className="max-h-[88vh] max-w-[92vw] rounded"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={current?.url}
          alt="attachment"
          className="max-h-[90vh] max-w-[92vw] object-contain rounded"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {multiple && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            viewerNext();
          }}
          className="absolute right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Next"
        >
          <ChevronRight size={28} />
        </button>
      )}
    </div>,
    document.body
  );
};

export default ImageViewer;
